"use client"

import { Toast as ToastPrimitive } from "@base-ui/react/toast"
import { cn } from "cn"

import { bottomInsetRef } from "@/lib/bottom-edge/writer"
import { toastManager, type ToastData } from "@/lib/toast"
import IconCheckSquareFill from "@/components/icons/check-square-fill"
import IconCross from "@/components/icons/cross"
import { resolveIconSpec, type IconComponent } from "@/components/icons/icon"
import IconInfoCircleFill from "@/components/icons/info-circle-fill"
import IconSpinner from "@/components/icons/spinner"
import IconWarningTriangleFill from "@/components/icons/warning-triangle-fill"
import { Button, ButtonProps } from "./button"
import { IconButton } from "./icon-button"

const TOAST_TYPE_ICON: Record<
  string,
  { icon: IconComponent; className: string }
> = {
  loading: {
    icon: IconSpinner,
    className: "animate-spin",
  },
  success: {
    icon: IconCheckSquareFill,
    className: "text-success",
  },
  error: {
    icon: IconWarningTriangleFill,
    className: "text-destructive",
  },
  info: {
    icon: IconInfoCircleFill,
    className: "text-primary",
  },
}

function ToastLeadingIcon({
  type,
  icon,
  className,
}: {
  type?: string
  icon?: ToastData["icon"]
  className?: string
}) {
  const spec = icon ?? (type ? TOAST_TYPE_ICON[type] : undefined)
  if (!spec) return null

  const { Icon, props } = resolveIconSpec(spec)
  return (
    <Icon
      {...props}
      aria-hidden
      size={16}
      className={cn("mt-px mr-0.5 shrink-0", props.className, className)}
    />
  )
}

function ToastProvider({ ...props }: ToastPrimitive.Provider.Props) {
  return <ToastPrimitive.Provider data-slot="toast-provider" {...props} />
}

function ToastPortal({ ...props }: ToastPrimitive.Portal.Props) {
  return <ToastPrimitive.Portal data-slot="toast-portal" {...props} />
}

function ToastViewport({ className, ...props }: ToastPrimitive.Viewport.Props) {
  return (
    <ToastPrimitive.Viewport
      ref={bottomInsetRef}
      data-slot="toast-viewport"
      className={cn(
        "pointer-events-none fixed inset-x-4 z-toast mx-auto w-auto max-w-sm outline-none sm:right-6 sm:left-auto sm:mx-0 sm:w-full",
        // --toast-bottom also feeds the roots' slide travel, so it must be
        // accurate at every breakpoint — a plain sm:bottom-6 would leave the
        // mobile formula in the var on desktop.
        "[--toast-bottom:calc(max(var(--bottom-inset,0px),env(safe-area-inset-bottom,0px))+1rem)]",
        "sm:[--toast-bottom:1.5rem]",
        "bottom-(--toast-bottom)",
        className
      )}
      {...props}
    />
  )
}

function ToastRoot({ className, ...props }: ToastPrimitive.Root.Props) {
  return (
    <ToastPrimitive.Root
      data-slot="toast-root"
      className={cn(
        "group/toast pointer-events-auto absolute right-0 bottom-0 z-[calc(1000-var(--toast-index))] w-full origin-bottom rounded-2xl bg-surface-2 text-foreground shadow-ring-xl will-change-transform outline-none select-none",
        "[--gap:0.75rem] [--height:var(--toast-frontmost-height,var(--toast-height))] [--offset-y:calc(var(--toast-offset-y)*-1+calc(var(--toast-index)*var(--gap)*-1)+var(--toast-swipe-movement-y))] [--peek:0.75rem] [--scale:calc(max(0,1-(var(--toast-index)*0.1)))] [--shrink:calc(1-var(--scale))]",
        // Downward enter/exit travel: 150% only clears the toast itself; the
        // viewport floats --toast-bottom above the screen edge (nav, sticky
        // bar), so without that term the exit ends resting on top of the nav.
        "[--offscreen-y:calc(150%+var(--toast-bottom,0px))]",
        "h-(--height) transform-[translateX(var(--toast-swipe-movement-x))_translateY(calc(var(--toast-swipe-movement-y)-(var(--toast-index)*var(--peek))-(var(--shrink)*var(--height))))_scale(var(--scale))] [transition:transform_500ms_cubic-bezier(0.22,1,0.36,1),opacity_250ms_ease-out,height_150ms]",
        // Exit gets its own transition: the base curve is a hard ease-out that
        // front-loads the travel (right for stack shuffling, abrupt as a
        // dismissal), and opacity runs shorter than transform so the fade
        // happens while the toast is still on screen.
        "data-ending-style:[transition:transform_600ms_ease-in-out,opacity_250ms_ease-out,height_150ms]",
        "after:absolute after:top-full after:left-0 after:h-[calc(var(--gap)+1px)] after:w-full after:content-['']",
        "data-expanded:h-(--toast-height) data-expanded:transform-[translateX(var(--toast-swipe-movement-x))_translateY(var(--offset-y))]",
        "data-limited:opacity-0 data-starting-style:transform-[translateY(var(--offscreen-y))]",
        "data-ending-style:opacity-0",
        "[&[data-ending-style]:not([data-limited]):not([data-swipe-direction])]:transform-[translateY(var(--offscreen-y))]",
        "data-ending-style:data-[swipe-direction=down]:transform-[translateY(calc(var(--toast-swipe-movement-y)+var(--offscreen-y)))]",
        "data-ending-style:data-[swipe-direction=left]:transform-[translateX(calc(var(--toast-swipe-movement-x)-150%))_translateY(var(--offset-y))]",
        "data-ending-style:data-[swipe-direction=right]:transform-[translateX(calc(var(--toast-swipe-movement-x)+150%))_translateY(var(--offset-y))]",
        "data-ending-style:data-[swipe-direction=up]:transform-[translateY(calc(var(--toast-swipe-movement-y)-150%))]",
        "data-expanded:data-ending-style:data-[swipe-direction=down]:transform-[translateY(calc(var(--toast-swipe-movement-y)+var(--offscreen-y)))]",
        "data-expanded:data-ending-style:data-[swipe-direction=left]:transform-[translateX(calc(var(--toast-swipe-movement-x)-150%))_translateY(var(--offset-y))]",
        "data-expanded:data-ending-style:data-[swipe-direction=right]:transform-[translateX(calc(var(--toast-swipe-movement-x)+150%))_translateY(var(--offset-y))]",
        "data-expanded:data-ending-style:data-[swipe-direction=up]:transform-[translateY(calc(var(--toast-swipe-movement-y)-150%))]",
        className
      )}
      {...props}
    />
  )
}

function ToastContent({ className, ...props }: ToastPrimitive.Content.Props) {
  return (
    <ToastPrimitive.Content
      data-slot="toast-content"
      className={cn(
        "relative flex items-start gap-2 p-3.5 text-sm",
        "transition-opacity duration-250 ease-[cubic-bezier(0.22,1,0.36,1)] data-behind:opacity-0 data-expanded:opacity-100",
        className
      )}
      {...props}
    />
  )
}

function ToastTitle({ className, ...props }: ToastPrimitive.Title.Props) {
  return (
    <ToastPrimitive.Title
      data-slot="toast-title"
      className={cn("leading-tight font-medium text-pretty", className)}
      {...props}
    />
  )
}

function ToastDescription({
  className,
  ...props
}: ToastPrimitive.Description.Props) {
  return (
    <ToastPrimitive.Description
      data-slot="toast-description"
      className={cn("leading-tight text-pretty whitespace-pre-line", className)}
      {...props}
    />
  )
}

function ToastAction({
  className,
  render = (
    <Button
      size="sm"
      variant="link"
      className="-mt-1 mr-auto -mb-1.5 -ml-(--button-sm-px)"
    />
  ),
  ...props
}: ToastPrimitive.Action.Props) {
  return (
    <ToastPrimitive.Action
      data-slot="toast-action"
      render={render}
      className={className}
      {...props}
    />
  )
}

function ToastCloseButton({
  className,
  variant = "ghost",
}: {
  className?: string
  variant?: ButtonProps["variant"]
}) {
  return (
    <ToastPrimitive.Close
      render={
        <IconButton
          size="xs"
          variant={variant}
          icon={{
            icon: IconCross,
            size: 12,
            strokeWidth: 2.5,
          }}
          label="Dismiss"
          className={cn("-mr-2 shrink-0", className)}
        />
      }
    />
  )
}

function ToastList() {
  const { toasts } = ToastPrimitive.useToastManager()

  return toasts.map((toast) => {
    const hasAction = !!toast.actionProps
    const isStandaloneText = !!toast.title !== !!toast.description
    const centerItems = !hasAction && isStandaloneText

    return (
      <ToastRoot key={toast.id} toast={toast}>
        <ToastContent className={centerItems ? "items-center" : undefined}>
          {toast.data?.leading ? (
            <span className="shrink-0">{toast.data.leading}</span>
          ) : (
            <ToastLeadingIcon type={toast.type} icon={toast.data?.icon} />
          )}
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            {toast.title ? <ToastTitle>{toast.title}</ToastTitle> : null}
            {toast.description ? (
              <ToastDescription>{toast.description}</ToastDescription>
            ) : null}
            {toast.actionProps ? <ToastAction /> : null}
          </div>
          <ToastCloseButton className={!centerItems ? "-mt-1" : undefined} />
        </ToastContent>
      </ToastRoot>
    )
  })
}

function Toaster({
  toastManager: manager = toastManager,
  timeout = 6000,
  limit = 3,
  ...props
}: ToastPrimitive.Provider.Props) {
  return (
    <ToastProvider
      toastManager={manager}
      timeout={timeout}
      limit={limit}
      {...props}
    >
      <ToastPortal>
        <ToastViewport>
          <ToastList />
        </ToastViewport>
      </ToastPortal>
    </ToastProvider>
  )
}

export {
  Toaster,
  ToastProvider,
  ToastPortal,
  ToastViewport,
  ToastRoot,
  ToastContent,
  ToastTitle,
  ToastDescription,
  ToastAction,
  ToastCloseButton,
  ToastLeadingIcon,
  ToastPrimitive,
}
